import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  FileSignature,
  FileText,
  Upload,
  Loader,
  X,
  Search,
  Eye,
  Pencil,
  ChevronRight,
  BookPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { formatCalendarDate } from '../lib/calendar-date';
import { fmtCurrency } from '../lib/display-labels';
import SortTh, { sortCompare, makeToggleSort } from '../components/SortTh';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { canEditPriceBook } from '../lib/price-book-missing';
import { useAuth } from '../contexts/AuthContext';

interface ParsedItem {
  itemType: string;
  serviceName: string;
  description: string | null;
  unit: string | null;
  price: number | null;
  revenueCode: string | null;
  capacity?: string | null;
}

interface ParsedSheet {
  name: string | null;
  currency: string | null;
  rateType: 'book_rate' | 'bid_contract' | null;
  isAllInBid: boolean;
  allInBidAmount: number | null;
  allInBidDescription: string | null;
  items: ParsedItem[];
  /**
   * Set when the parse is known to be incomplete — the model stopped mid-JSON
   * and we salvaged what we could, or the input was sliced before it was sent.
   * Optional because a response predating the field is absent, not `false`.
   */
  truncated?: boolean;
  /** How many line items the parser actually returned. */
  parsedCount?: number;
}

type ImportState = 'idle' | 'uploading' | 'preview' | 'saving';

type FilterKey = 'all' | 'active' | 'inactive' | 'book_rate' | 'bid_contract';

const sheetRateType = (s: any): string | undefined => s.rateType || s.rate_type;

/**
 * The chips above the list, each carrying the ONE predicate both the list and the
 * chip's count use — so a chip cannot say `Active 13` over a list that shows
 * twelve.
 *
 * THE COUNTS ARE A TALLY OF THE LOADED ROWS, and that is not the client-side
 * recomputation CLAUDE.md forbids. `GET /rate-sheets` returns every sheet for
 * the company with no LIMIT and no pagination, and no endpoint returns these
 * figures, so the loaded list is the only source there is — verified in
 * rate-sheets.routes.ts, not assumed. Quotes cannot do this (its list is
 * status-filtered server-side) and reads its dashboard endpoint instead; the
 * two pages differ here because their APIs do.
 *
 * Like Quotes, the counts are company-wide: they do NOT follow the search box.
 */
const FILTERS: { key: FilterKey; label: string; match: (s: any) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'active', label: 'Active', match: (s) => s.status === 'active' },
  { key: 'inactive', label: 'Inactive', match: (s) => s.status === 'inactive' },
  { key: 'book_rate', label: 'Book Rate', match: (s) => sheetRateType(s) === 'book_rate' },
  { key: 'bid_contract', label: 'Bid / Contract', match: (s) => sheetRateType(s) === 'bid_contract' },
];

/**
 * The status pill: a saturated fill with dark ink, the Quotes treatment.
 *
 * NOT EXHAUSTIVE BY CONSTRUCTION, unlike Quotes. `rate_sheets.status` is a
 * free varchar(20), not an enum. Production holds exactly `active` (13) and
 * `inactive` (1) across all tenants, measured 2026-09-12, and both writers in
 * the API stamp `active`. A value outside the map renders a slate pill reading
 * an em dash and NO rail — the screen used to call any non-active value
 * "Inactive", which is a guess dressed as a label.
 *
 * The fills are the theme-independent signal hues for the reason Quotes gives:
 * a pill carries its own fill, so one dark ink has to hold in both themes.
 */
const SHEET_STATUS: Record<string, { label: string; fill: string }> = {
  active: { label: 'Active', fill: 'bg-fi-signal-green' },
  inactive: { label: 'Inactive', fill: 'bg-fi-signal-slate' },
};

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate' };

function SheetStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = (status && SHEET_STATUS[status]) || UNKNOWN_STATUS;
  return (
    <span
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * The 4px row rail, same width and same theme-following hues as Quotes. An
 * inactive sheet is a dead row, so it gets the quiet steel edge rather than a
 * warning hue; an unknown status gets no colour at all, keeping the width.
 */
const STATUS_RAIL: Record<string, string> = {
  active: 'border-l-fi-green',
  inactive: 'border-l-fi-steel',
};

/** Existing labels, unchanged; an unrecognised rate type is a gap, not a guess. */
const RATE_TYPE_LABEL: Record<string, string> = {
  book_rate: 'Book',
  bid_contract: 'Contract',
};

function formatDate(dateStr: string | null | undefined): string {
  return formatCalendarDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '';
}

/** Whole-dollar money. @see fmtCurrency — absence renders '—', not '$0'. */
const formatPrice = (amount: number | string | null | undefined) => fmtCurrency(amount, null, 0);

export default function RateSheetsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [importState, setImportState] = useState<ImportState>('idle');

  // Preview modal state
  const [previewName, setPreviewName] = useState('');
  const [previewCurrency, setPreviewCurrency] = useState('CAD');
  const [previewRateType, setPreviewRateType] = useState<'book_rate' | 'bid_contract'>('book_rate');
  const [previewCustomerId, setPreviewCustomerId] = useState('');
  const [previewItems, setPreviewItems] = useState<ParsedItem[]>([]);
  // What the PARSER returned, frozen at parse time. Deliberately not derived
  // from previewItems — the admin can delete rows here, and "N items found"
  // has to keep describing the document, not the current edit state.
  const [previewParsedCount, setPreviewParsedCount] = useState(0);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');

  // ─── Queries & Mutations ───────────────────────────────────────────

  const { data: sheets, isLoading } = useQuery({
    queryKey: ['rate-sheets'],
    queryFn: () => api.rateSheets.list().then((r) => r.data.data ?? []),
    staleTime: 0,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.customers ?? []);
    }),
    enabled: importState === 'preview' || importState === 'saving',
  });

  const createCustomerMutation = useMutation({
    mutationFn: (name: string) => api.customers.create({ name }),
    onSuccess: (res) => {
      const newCust = unwrap<{ id: string }>(res.data);
      // The write was accepted, so refresh the list either way — the customer
      // exists even when the response carried no payload to identify it by.
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (!newCust?.id) {
        // Nothing to preselect. Leave the form open and say so rather than
        // calling setPreviewCustomerId(undefined) and silently clearing the
        // admin's selection on the sheet they are about to save.
        setImportError('The customer was created but the server returned no id — pick it from the list.');
        return;
      }
      setPreviewCustomerId(newCust.id);
      setShowNewCustomer(false);
      setNewCustomerName('');
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => api.rateSheets.importFile(file),
    onSuccess: (res) => {
      const body = res.data;
      const parsed: ParsedSheet = body.data;
      setPreviewName(parsed.name || 'Imported Rate Sheet');
      setPreviewCurrency(parsed.currency || 'CAD');
      setPreviewRateType(parsed.rateType || 'book_rate');
      const items = Array.isArray(parsed.items) ? parsed.items : [];
      setPreviewItems(items);

      // `truncated` / `parsedCount` ride the envelope as SIBLINGS of `data`,
      // and the parser also stamps them onto the sheet itself. Read the
      // envelope first and fall back to the payload — reading only `data.…` is
      // exactly how the sibling `duplicateWarning` ended up dead in
      // ImportScreen, silently, with nothing to see on screen.
      setPreviewTruncated((body.truncated ?? parsed.truncated) === true);
      const count = body.parsedCount ?? parsed.parsedCount;
      // An API predating the field sends nothing — count the rows we did get
      // rather than rendering a confident 0.
      setPreviewParsedCount(Number.isFinite(count) ? Number(count) : items.length);

      setImportError(null);
      setImportState('preview');
    },
    onError: (err: any) => {
      setImportError(err.response?.data?.error || 'Failed to parse file');
      setImportState('idle');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (data: any) => api.rateSheets.confirmImport(data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['rate-sheets'] });
      resetImport();
    },
    onError: (err: any) => {
      setImportError(err.response?.data?.error || 'Failed to save rate sheet');
      setImportState('preview');
    },
  });

  // ─── Filtered sheets ──────────────────────────────────────────────

  const filteredSheets = useMemo(() => {
    if (!sheets) return [];
    let result = sheets as any[];

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (s: any) =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.customerName || s.customer_name || '').toLowerCase().includes(q)
      );
    }

    // Tab filter — the same predicate the chip counts use
    const active = FILTERS.find((f) => f.key === filter);
    if (active) result = result.filter(active.match);

    return result;
  }, [sheets, search, filter]);

  /** Per-chip tallies over the whole list; `null` until the list has arrived. */
  const filterCounts = useMemo(() => {
    if (!sheets) return null;
    const all = sheets as any[];
    return Object.fromEntries(FILTERS.map((f) => [f.key, all.filter(f.match).length])) as Record<FilterKey, number>;
  }, [sheets]);

  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  const sortedSheets = useMemo(() => {
    return [...filteredSheets].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortBy) {
        case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
        case 'type': aVal = a.rateType || a.rate_type || ''; bVal = b.rateType || b.rate_type || ''; break;
        case 'customer': aVal = a.customerName || a.customer_name || ''; bVal = b.customerName || b.customer_name || ''; break;
        case 'items': aVal = a.itemCount ?? a.item_count ?? 0; bVal = b.itemCount ?? b.item_count ?? 0; break;
        case 'effective': aVal = a.effectiveDate || a.effective_date || ''; bVal = b.effectiveDate || b.effective_date || ''; break;
        case 'expiry': aVal = a.expiryDate || a.expiry_date || ''; bVal = b.expiryDate || b.expiry_date || ''; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        default: return 0;
      }
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [filteredSheets, sortBy, sortDir]);

  // ─── Handlers ──────────────────────────────────────────────────────

  function resetImport() {
    setImportState('idle');
    setPreviewName('');
    setPreviewCurrency('CAD');
    setPreviewRateType('book_rate');
    setPreviewCustomerId('');
    setPreviewItems([]);
    setPreviewParsedCount(0);
    setPreviewTruncated(false);
    setImportError(null);
    setShowNewCustomer(false);
    setNewCustomerName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportState('uploading');
    setImportError(null);
    importMutation.mutate(file);
  }

  function handleConfirm() {
    setImportState('saving');
    confirmMutation.mutate({
      name: previewName,
      currency: previewCurrency,
      rateType: previewRateType,
      customerId: previewCustomerId || null,
      items: previewItems,
    });
  }

  function updateItem(index: number, field: keyof ParsedItem, value: any) {
    setPreviewItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  }

  function removeItem(index: number) {
    setPreviewItems((prev) => prev.filter((_, i) => i !== index));
  }

  // ─── Render ────────────────────────────────────────────────────────

  const openSheet = (id: string) => navigate(`/rate-sheets/${id}`);

  /**
   * Enter and Space open a row — but only when the ROW has focus.
   *
   * These rows hold their own View and Edit buttons, which Quotes' rows do
   * not. A key event on either of them bubbles here, and handling it would
   * `preventDefault` the button's own activation: Enter on Edit would open the
   * read view instead. Hence the target check, and hence no `role="button"` on
   * the row either — an explicit button role makes its children
   * presentational, and the two buttons inside would stop being announced.
   */
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSheet(id);
    }
  };

  const countOf = (key: FilterKey) => (filterCounts ? filterCounts[key] : undefined);

  return (
    /* THE A/B AGAINST QUOTES IS SETTLED, AND THIS SIDE WON. Same band, same
       chips, same register as Quotes, over the grey-and-white body (the
       --fieldiq-* surface, ink and edge tokens). The cream --fi-* canvas this
       page was tested against is gone from Quotes too, so the two pages now
       share one body palette as well as the navy band and the amber accents. */
    <div data-testid="rate-sheets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Price book"
        title="Rate Sheets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            {/* No colour class on this one: it inherits the band's near-white,
                which theme.css holds up on the navy fill in light mode. A plain
                white-text utility here would be remapped to the heading colour. */}
            {/* The way in to /rates/missing. Same four roles the API allows it to. */}
            {canEditPriceBook(user?.role) && (
              <button
                type="button"
                onClick={() => navigate('/rates/missing')}
                className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
              >
                <BookPlus className="size-4" aria-hidden="true" />
                Missing from book
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/import/rate-sheets')}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Upload className="size-4" aria-hidden="true" />
              Import
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Upload className="size-4" aria-hidden="true" />
              + New Rate Sheet
            </button>
          </>
        }
        /* Tallies of the list this screen already loads — no added query. See
           FILTERS for why a tally is the honest source here. Absent while the
           list is in flight or failed, and displayValue() renders that as a
           gap rather than a zero. */
        stats={[
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Rate Sheets',
            sub: 'All clients',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(countOf('all')),
          },
          {
            icon: <CheckCircle className="size-4" aria-hidden="true" />,
            label: 'Active',
            sub: 'Available to price',
            tone: 'success',
            loading: isLoading,
            value: displayValue(countOf('active')),
          },
          {
            icon: <BookOpen className="size-4" aria-hidden="true" />,
            label: 'Book Rate',
            sub: 'Standard pricing',
            tone: 'info',
            loading: isLoading,
            value: displayValue(countOf('book_rate')),
          },
          {
            icon: <FileSignature className="size-4" aria-hidden="true" />,
            label: 'Bid / Contract',
            sub: 'Negotiated pricing',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(countOf('bid_contract')),
          },
        ]}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xlsx,.xls,.csv,.docx"
        className="hidden"
        onChange={handleFileChange}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Error banner */}
          {importError && importState === 'idle' && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm flex items-center justify-between">
              <span>{importError}</span>
              <button onClick={() => setImportError(null)}>
                <X size={16} />
              </button>
            </div>
          )}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {f.label}
                    {/* No count is claimed while the list is in flight. */}
                    {!isLoading && (
                      <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                        {displayValue(countOf(f.key))}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search rate sheets..."
                aria-label="Search rate sheets"
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-fi-control pl-10 pr-8 py-1.5 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:border-fi-amber"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-muted hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* The register. The amber rail is on this container and nowhere
              else; rows carry a status rail, never a second amber one. */}
          <section
            aria-labelledby="rate-sheet-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Sheet register</p>
              <h2 id="rate-sheet-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">Book rates and contracts</h2>
            </header>

            {/* Loading skeleton */}
            {isLoading && (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-fi-panel bg-fieldiq-dark/60 p-4 animate-pulse">
                    <div className="h-4 w-1/3 rounded bg-fieldiq-border" />
                    <div className="mt-2 h-3 w-1/4 rounded bg-fieldiq-border" />
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isLoading && sheets && sheets.length === 0 && (
              <div className="px-4 py-16 text-center">
                <FileText size={40} className="mx-auto mb-3 text-fieldiq-muted" />
                <p className="text-white text-lg mb-2">No rate sheets yet</p>
                <p className="text-fieldiq-muted text-sm">
                  Import a PDF, Excel, CSV, or Word rate sheet to get started.
                </p>
              </div>
            )}

            {/* No results for search/filter */}
            {!isLoading && sheets && sheets.length > 0 && filteredSheets.length === 0 && (
              <div className="px-4 py-16 text-center">
                <Search size={40} className="mx-auto mb-3 text-fieldiq-muted" />
                <p className="text-white text-lg mb-2">No matching rate sheets</p>
                <p className="text-fieldiq-muted text-sm">
                  Try adjusting your search or filters.
                </p>
              </div>
            )}

            {!isLoading && filteredSheets.length > 0 && (
              <>
                {/* Desktop Table */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      {/* Transparent 4px on the head so its columns line up
                          with the railed rows below. */}
                      <tr className="border-l-4 border-l-transparent">
                        <SortTh label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Type" field="type" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Client" field="customer" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Items" field="items" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Effective" field="effective" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Expiry" field="expiry" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th className="text-left text-xs uppercase tracking-wider font-bold text-fieldiq-muted px-4 py-3 w-[100px]">Actions</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSheets.map((sheet: any) => {
                        const rateType = sheetRateType(sheet);
                        const customerName = sheet.customerName || sheet.customer_name;
                        const effectiveDate = sheet.effectiveDate || sheet.effective_date;
                        const expiryDate = sheet.expiryDate || sheet.expiry_date;
                        const itemCount = sheet.itemCount ?? sheet.item_count ?? 0;
                        const isAllIn = sheet.isAllInBid || sheet.is_all_in_bid;
                        const allInAmount = sheet.allInBidAmount || sheet.all_in_bid_amount;

                        return (
                          <tr
                            key={sheet.id}
                            tabIndex={0}
                            onClick={() => openSheet(sheet.id)}
                            onKeyDown={rowKeys(sheet.id)}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                              STATUS_RAIL[sheet.status] ?? 'border-l-transparent'
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FileText size={14} className="text-fi-amber flex-shrink-0" />
                                <span className="text-sm font-extrabold text-white">{sheet.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text">
                                {(rateType && RATE_TYPE_LABEL[rateType]) || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-fieldiq-text">{customerName || 'All'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {isAllIn && allInAmount != null ? (
                                <span className="text-fi-amber font-extrabold">{formatPrice(allInAmount)} All-In</span>
                              ) : (
                                `${itemCount} item${itemCount !== 1 ? 's' : ''}`
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-muted font-mono">{effectiveDate ? formatDate(effectiveDate) : '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-muted font-mono">{expiryDate ? formatDate(expiryDate) : '—'}</td>
                            <td className="px-4 py-3">
                              <SheetStatusBadge status={sheet.status} />
                            </td>
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => navigate(`/rate-sheets/${sheet.id}`)}
                                  aria-label={`View ${sheet.name}`}
                                  className="p-1.5 rounded-fi-control hover:bg-fieldiq-dark text-fieldiq-muted hover:text-white transition-colors"
                                >
                                  <Eye size={13} />
                                </button>
                                <button
                                  onClick={() => navigate(`/rate-sheets/${sheet.id}?edit=true`)}
                                  aria-label={`Edit ${sheet.name}`}
                                  className="p-1.5 rounded-fi-control hover:bg-fieldiq-dark text-fieldiq-muted hover:text-white transition-colors"
                                >
                                  <Pencil size={13} />
                                </button>
                              </div>
                            </td>
                            <td className="pr-3 py-3">
                              <ChevronRight size={14} className="text-fieldiq-muted" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards — no amber rail here; that edge belongs to the
                    surrounding register, never to a row. */}
                <div className="md:hidden space-y-3 p-4">
                  {sortedSheets.map((sheet: any) => {
                    const rateType = sheetRateType(sheet);
                    const customerName = sheet.customerName || sheet.customer_name;
                    const effectiveDate = sheet.effectiveDate || sheet.effective_date;
                    const expiryDate = sheet.expiryDate || sheet.expiry_date;
                    const itemCount = sheet.itemCount ?? sheet.item_count ?? 0;
                    const currency = sheet.currency || 'CAD';
                    const isAllIn = sheet.isAllInBid || sheet.is_all_in_bid;
                    const allInAmount = sheet.allInBidAmount || sheet.all_in_bid_amount;

                    return (
                      <div
                        key={sheet.id}
                        className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-fi-control bg-fieldiq-dark flex items-center justify-center flex-shrink-0">
                            <FileText size={20} className="text-fi-amber" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-extrabold text-white truncate">{sheet.name}</p>
                          </div>
                          <SheetStatusBadge status={sheet.status} />
                        </div>
                        <div className="space-y-1 mb-3 pl-[52px]">
                          <p className="text-sm text-fieldiq-text">
                            {rateType === 'bid_contract' ? 'Bid / Contract' : 'Standard'}
                            {' · '}
                            {customerName || 'All Clients'}
                          </p>
                          <p className="text-sm text-fieldiq-text">
                            {itemCount} item{itemCount !== 1 ? 's' : ''}
                            {' · '}
                            Currency: {currency}
                          </p>
                          {effectiveDate && (
                            <p className="text-sm text-fieldiq-text">
                              Effective: {formatDate(effectiveDate)}
                              {expiryDate && ` — Expires: ${formatDate(expiryDate)}`}
                            </p>
                          )}
                          {isAllIn && allInAmount != null && (
                            <p className="text-sm font-semibold text-fi-green">
                              {formatPrice(allInAmount)} {currency} (All-In)
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 pl-[52px]">
                          <button
                            onClick={() => navigate(`/rate-sheets/${sheet.id}`)}
                            className="flex items-center gap-1.5 px-3.5 py-1.5 border border-fieldiq-border bg-fieldiq-dark rounded-fi-pill text-sm font-bold text-fieldiq-text hover:text-white transition-colors"
                          >
                            <Eye size={13} /> View
                          </button>
                          <button
                            onClick={() => navigate(`/rate-sheets/${sheet.id}?edit=true`)}
                            className="flex items-center gap-1.5 px-3.5 py-1.5 border border-fieldiq-border bg-fieldiq-dark rounded-fi-pill text-sm font-bold text-fieldiq-text hover:text-white transition-colors"
                          >
                            <Pencil size={13} /> Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>
      </section>

      {/* ─── Upload / Preview Modal ─────────────────────────────────── */}
      {(importState === 'uploading' || importState === 'preview' || importState === 'saving') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-[95vw] mx-4 max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
              <h3 className="text-lg font-bold text-white">
                {importState === 'uploading' && 'Parsing Rate Sheet...'}
                {(importState === 'preview' || importState === 'saving') && 'Review Imported Rate Sheet'}
              </h3>
              {importState !== 'uploading' && importState !== 'saving' && (
                <button onClick={resetImport} className="text-white hover:text-white">
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Uploading spinner */}
            {importState === 'uploading' && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader size={40} className="animate-spin text-fieldiq-accent" />
                <p className="text-white">Parsing rate sheet with AI...</p>
                <p className="text-white text-xs">This may take a few seconds</p>
              </div>
            )}

            {/* Preview / Edit */}
            {(importState === 'preview' || importState === 'saving') && (
              <>
                {/* Top fields */}
                <div className="px-6 py-4 space-y-3 border-b border-fieldiq-border">
                  {importError && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
                      {importError}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label className="text-sm text-white block mb-1">
                        Rate Sheet Name
                      </label>
                      <input
                        type="text"
                        value={previewName}
                        onChange={(e) => setPreviewName(e.target.value)}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                        disabled={importState === 'saving'}
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="rs-preview-client" className="text-sm text-white block mb-1">Client</label>
                      {!showNewCustomer ? (
                        <select
                          id="rs-preview-client"
                          value={previewCustomerId}
                          onChange={(e) => {
                            if (e.target.value === '__new__') {
                              setShowNewCustomer(true);
                            } else {
                              setPreviewCustomerId(e.target.value);
                            }
                          }}
                          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                          disabled={importState === 'saving'}
                        >
                          <option value="">All Clients</option>
                          {customers.map((c: any) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                          <option value="__new__">+ Add New Client</option>
                        </select>
                      ) : (
                        <div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newCustomerName}
                              onChange={(e) => { setNewCustomerName(e.target.value); createCustomerMutation.reset(); }}
                              placeholder="Client name"
                              className="flex-1 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newCustomerName.trim()) {
                                  createCustomerMutation.mutate(newCustomerName.trim());
                                }
                                if (e.key === 'Escape') {
                                  setShowNewCustomer(false);
                                  setNewCustomerName('');
                                  createCustomerMutation.reset();
                                }
                              }}
                            />
                            <button
                              onClick={() => {
                                if (newCustomerName.trim()) {
                                  createCustomerMutation.mutate(newCustomerName.trim());
                                }
                              }}
                              disabled={!newCustomerName.trim() || createCustomerMutation.isPending}
                              className="px-3 py-2 bg-fieldiq-accent text-black text-sm font-semibold rounded-lg disabled:opacity-50"
                            >
                              {createCustomerMutation.isPending ? '...' : 'Add'}
                            </button>
                            <button
                              onClick={() => { setShowNewCustomer(false); setNewCustomerName(''); createCustomerMutation.reset(); }}
                              className="px-2 py-2 text-white text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                          {/*
                            Rendered HERE, not through `importError`. That banner is
                            gated on `importState === 'idle'` and this control lives
                            inside the preview flow, so a refusal routed there would
                            be invisible — which is the bug, moved rather than fixed.
                          */}
                          {createCustomerMutation.isError && (
                            <p className="text-xs text-red-400 mt-1">
                              {getApiErrorMessage(createCustomerMutation.error, 'Failed to create client')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="w-24">
                      <label className="text-sm text-white block mb-1">Currency</label>
                      <select
                        value={previewCurrency}
                        onChange={(e) => setPreviewCurrency(e.target.value)}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                        disabled={importState === 'saving'}
                      >
                        <option value="CAD">CAD</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    <div className="w-40">
                      <label className="text-sm text-white block mb-1">Rate Type</label>
                      <select
                        value={previewRateType}
                        onChange={(e) =>
                          setPreviewRateType(e.target.value as 'book_rate' | 'bid_contract')
                        }
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                        disabled={importState === 'saving'}
                      >
                        <option value="book_rate">Book Rate</option>
                        <option value="bid_contract">Bid / Contract</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Parse summary — the count is always visible so a short list
                    is legible as a short list. The banner is additional, and
                    only when the API positively says the parse was cut off. */}
                <div className="px-6 pt-3 space-y-3">
                  {previewTruncated && (
                    <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg p-3 text-sm flex items-start gap-2">
                      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">
                          This document was too large to fully process. Only {previewParsedCount} item
                          {previewParsedCount !== 1 ? 's' : ''} were extracted.
                        </p>
                        <p className="text-amber-400/80 text-xs mt-1">
                          Split the file into smaller sections and import each part separately.
                        </p>
                      </div>
                    </div>
                  )}
                  <p className="text-sm text-fieldiq-muted">
                    {previewParsedCount} item{previewParsedCount !== 1 ? 's' : ''} found
                  </p>
                </div>

                {/* Items table */}
                <div className="flex-1 overflow-auto px-6 py-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white text-xs">
                        <th className="text-left pb-2 pr-2">Type</th>
                        <th className="text-left pb-2 pr-2">Service Name</th>
                        <th className="text-left pb-2 pr-2">Description</th>
                        <th className="text-left pb-2 pr-2">Unit</th>
                        <th className="text-right pb-2 pr-2">Price</th>
                        <th className="text-left pb-2 pr-2">Rev Code</th>
                        <th className="pb-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-fieldiq-border">
                      {previewItems.map((item, i) => (
                        <tr key={i}>
                          <td className="py-1.5 pr-2">
                            <select
                              value={item.itemType}
                              onChange={(e) => updateItem(i, 'itemType', e.target.value)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-1.5 py-1 text-sm text-white w-full"
                              disabled={importState === 'saving'}
                            >
                              <option value="service">Service</option>
                              <option value="equipment">Equipment</option>
                              <option value="labour">Labour</option>
                              <option value="materials">Materials</option>
                              <option value="other">Other</option>
                            </select>
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.serviceName}
                              onChange={(e) => updateItem(i, 'serviceName', e.target.value)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.description || ''}
                              onChange={(e) =>
                                updateItem(i, 'description', e.target.value || null)
                              }
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.unit || ''}
                              onChange={(e) => updateItem(i, 'unit', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="number"
                              value={item.price ?? ''}
                              onChange={(e) =>
                                updateItem(
                                  i,
                                  'price',
                                  e.target.value === '' ? null : Number(e.target.value)
                                )
                              }
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-20 text-right"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.revenueCode || ''}
                              onChange={(e) =>
                                updateItem(i, 'revenueCode', e.target.value || null)
                              }
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5">
                            <button
                              onClick={() => removeItem(i)}
                              className="text-white hover:text-red-400"
                              disabled={importState === 'saving'}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewItems.length === 0 && (
                    <p className="text-center text-white text-sm py-8">
                      No items parsed. Try a different file.
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-fieldiq-border">
                  <p className="text-white text-xs">
                    {previewItems.length} item{previewItems.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={resetImport}
                      className="px-4 py-2 text-sm text-white hover:text-white transition-colors"
                      disabled={importState === 'saving'}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={
                        importState === 'saving' ||
                        !previewName.trim() ||
                        previewItems.length === 0
                      }
                      className="flex items-center gap-2 px-5 py-2 bg-fieldiq-accent text-black font-semibold rounded-lg text-sm hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importState === 'saving' && (
                        <Loader size={14} className="animate-spin" />
                      )}
                      {importState === 'saving' ? 'Saving...' : 'Confirm & Save'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
